import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../navbar/Navbar";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Heading,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Avatar,
  Tag,
  useToast,
  Spinner,
  Divider,
  Center,
} from "@chakra-ui/react";

export default function CounsellorAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const counsellor = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    axios
      .get(`https://happyu.onrender.com/counsellor/${counsellor._id}/appointments`)
      .then((res) => setAppointments(res.data))
      .catch(() =>
        toast({
          title: "Error fetching appointments",
          status: "error",
          duration: 3000,
          isClosable: true,
        })
      )
      .finally(() => setLoading(false));
  }, [counsellor._id, toast]);

  const updateStatus = async (id, status, userId) => {
    try {
      await axios.post(`https://happyu.onrender.com/appointments/update/${id}`, {
        status,
      });

      setAppointments((prev) =>
        prev.map((a) => (a._id === id ? { ...a, status } : a))
      );

      toast({
        title:
          status === "accepted"
            ? "Appointment accepted"
            : "Appointment declined",
        status: status === "accepted" ? "success" : "info",
        duration: 2000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Error updating status",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading)
    return (
      <Center h="100vh">
        <VStack>
          <Spinner size="xl" color="blue.500" />
          <Text mt={2}>Loading appointments...</Text>
        </VStack>
      </Center>
    );

  return (
    <>
      <Box bg="gray.50" minH="100vh" p={6} className="mt-16">
        <VStack spacing={8} align="stretch" maxW="800px" mx="auto" mt={8}>
          <Heading textAlign="center" color="blue.700">
            Pending Appointments
          </Heading>
          <Text textAlign="center" fontSize="lg" color="gray.600">
            Manage and respond to appointment requests
          </Text>

          {appointments.length === 0 ? (
            <Center>
              <Text fontSize="md" color="gray.500">
                No appointments yet.
              </Text>
            </Center>
          ) : (
            appointments.map((a) => (
              <Card
                key={a._id}
                bg="white"
                shadow="md"
                borderRadius="xl"
                _hover={{ shadow: "lg", transform: "scale(1.01)" }}
                transition="0.2s"
              >
                <CardHeader>
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <Avatar name={a.username} />
                      <Box>
                        <Text fontWeight="bold" fontSize="lg">
                          {a.username}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {a.date} — {a.time}
                        </Text>
                      </Box>
                    </HStack>
                    <Tag
                      size="md"
                      colorScheme={
                        a.status === "accepted"
                          ? "green"
                          : a.status === "declined"
                            ? "red"
                            : "yellow"
                      }
                    >
                      {a.status.toUpperCase()}
                    </Tag>
                  </HStack>
                </CardHeader>

                <Divider />

                <CardBody>
                  <Text color="gray.600" fontSize="sm">
                    Appointment ID: {a._id}
                  </Text>
                </CardBody>

                <CardFooter justify="flex-end">
                  {a.status === "accepted" ? (
                    <HStack spacing={3}>
                      <Button
                        colorScheme="blue"
                        onClick={() => {
                          const room = [a.userId, counsellor._id].join("-");
                          navigate(`/chat/${room}`);
                        }}
                      >
                        Chat
                      </Button>
                      <Button
                        colorScheme="purple"
                        onClick={() => {
                          const room = [a.userId, counsellor._id].sort().join("-");
                          navigate(`/video/${room}`);
                        }}
                      >
                        Video Call
                      </Button>
                    </HStack>
                  ) : a.status === "declined" ? (
                    <Text color="red.500" fontWeight="semibold">
                      Appointment declined
                    </Text>
                  ) : (
                    <HStack spacing={3}>
                      <Button
                        colorScheme="green"
                        onClick={() =>
                          updateStatus(a._id, "accepted", a.userId)
                        }
                      >
                        Accept
                      </Button>
                      <Button
                        colorScheme="red"
                        onClick={() =>
                          updateStatus(a._id, "declined", a.userId)
                        }
                      >
                        Decline
                      </Button>
                    </HStack>
                  )}
                </CardFooter>
              </Card>
            ))
          )}
        </VStack>
      </Box>
    </>
  );
}
